import React from 'react'
import { cn } from '@/lib/utils'

interface GhostButtonProps {
  children: React.ReactNode
  sub?: string
  size?: "lg" | "md"
  invert?: boolean
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  className?: string
}

/*
  GhostButton — outlined variant of PrimaryButton. Sizes mirror PrimaryButton
  exactly (padding + text size) so when the two appear side-by-side as CTA
  pairs (HomeHero, ServiceCTA, etc.) their heights match instead of one
  being a tall hand-lettered block and the other being a short pill.
*/
export function GhostButton({
  children,
  sub,
  size = "md",
  invert = false,
  onClick,
  className,
}: GhostButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full sm:inline-flex sm:w-auto flex-col items-start gap-0.5 cursor-pointer text-left",
        "bg-transparent rounded-[var(--radius)]",
        "font-body font-medium",
        "transition-colors",
        "focus-visible:outline-2 focus-visible:outline-offset-2",
        invert
          ? "text-secondary border border-secondary/50 hover:bg-secondary/10 focus-visible:outline-secondary"
          : "text-primary border border-primary hover:bg-primary/5 focus-visible:outline-primary",
        size === "lg" ? "px-6 py-5" : "px-5 py-3.5",
        className
      )}
    >
      <span className={cn("font-semibold", size === "lg" ? "text-lg" : "text-[15px]")}>
        {children}
      </span>
      {sub && (
        <span className={cn("opacity-70", size === "lg" ? "text-sm" : "text-xs")}>
          {sub}
        </span>
      )}
    </button>
  )
}
